// =====================================================================
// Faz U (2026-05-06) — Email Campaign Performance
// =====================================================================
// 3,303 lead'e gönderilen mailin gerçek ROI'sını ölçer:
//   Sent → Delivered → Opened → Clicked → (Sipariş)
//
// Brevo SMTP webhook'tan gelen event'ler email_events tablosunda biriken
// veriyle her kampanyanın open rate / click rate / bounce rate hesaplanır.
//
// Webhook setup: Owner Brevo paneline gidip aşağıdaki URL'i ekler:
//   https://api.miksanplastik.com.tr/api/email-webhook
// Sayfada "Webhook bağlı mı?" göstergesi var — Brevo paneline yönlendirme.
//
// Endpoint: GET /api/admin/insights/email-performance
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmailIcon from "@mui/icons-material/Email";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import API from "../services/api";
import toast from "react-hot-toast";

// ──────────────────────────────────────────────────────────────────────
// Yardımcı formatter'lar
// ──────────────────────────────────────────────────────────────────────
const fmtNum = (v) => Number(v || 0).toLocaleString("tr-TR");
const fmtDate = (s) => s ? new Date(s).toLocaleString("tr-TR", {
  day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
}) : "—";

// Open rate / click rate için renk eşiği (B2B sektör tipiği)
// Opened: %15-25 normal, %30+ çok iyi
const openColor = (pct) => {
  if (pct >= 30) return "#10b981";
  if (pct >= 15) return "#3b82f6";
  if (pct >= 5)  return "#f59e0b";
  return "#94a3b8";
};
// Clicked: %2-5 normal, %7+ çok iyi
const clickColor = (pct) => {
  if (pct >= 7)  return "#10b981";
  if (pct >= 2)  return "#3b82f6";
  if (pct >= 0.5) return "#f59e0b";
  return "#94a3b8";
};
// Bounce rate ters: düşük = iyi, yüksek = kötü
const bounceColor = (pct) => {
  if (pct <= 2)  return "#10b981";
  if (pct <= 5)  return "#3b82f6";
  if (pct <= 10) return "#f59e0b";
  return "#ef4444";
};

export default function EmailPerformance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    API.get("/api/admin/insights/email-performance")
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Webhook URL'ini panoya kopyala — Brevo'ya yapıştırması kolay olsun
  const copyWebhookUrl = () => {
    if (!data?.webhook?.webhook_url) return;
    navigator.clipboard.writeText(data.webhook.webhook_url);
    toast.success("Webhook URL kopyalandı — Brevo paneline yapıştırın");
  };

  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;
  if (!data) return null;

  const o = data.overall;
  // Genel funnel oran hesabı (delivered yoksa sent kullan)
  const denom = o.delivered > 0 ? o.delivered : o.smtp_ok;
  const overallOpenRate = denom > 0 ? Math.round((o.opened / denom) * 1000) / 10 : 0;
  const overallClickRate = denom > 0 ? Math.round((o.clicked / denom) * 1000) / 10 : 0;
  const overallBounceRate = o.smtp_ok > 0 ? Math.round((o.bounced / o.smtp_ok) * 1000) / 10 : 0;

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Üst başlık */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Email Campaign Performance</Typography>
          <Typography variant="body2" color="text.secondary">
            Brevo webhook ile open/click/bounce takibi — son 30 gün
          </Typography>
        </Box>
        <Tooltip title="Yenile">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {/* Webhook durum bilgi alert'i */}
      {!data.webhook.active ? (
        // Webhook henüz event almamış: kullanıcıya setup talimatı
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography fontWeight={700} gutterBottom>⚠️ Webhook henüz aktif değil</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Brevo'dan gelen open/click event'leri görmek için webhook'u kurman gerek:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "white", p: 1, borderRadius: 1, my: 1 }}>
            <Typography fontFamily="monospace" fontSize={12} sx={{ flex: 1, wordBreak: "break-all" }}>
              {data.webhook.webhook_url}
            </Typography>
            <IconButton size="small" onClick={copyWebhookUrl} title="Kopyala">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ fontSize: 13, mt: 1 }}>
            <strong>Adımlar:</strong> Brevo paneline git → Settings → Transactional → Settings → Webhook →
            "+ Add new webhook" → URL yukarıdaki, tüm event'leri (delivered/opened/click/bounce) seç → Save
          </Typography>
        </Alert>
      ) : (
        // Webhook aktif: yeşil onay
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3, borderRadius: 2 }}>
          ✅ Webhook aktif — son event: <strong>{fmtDate(data.webhook.last_event_at)}</strong>
        </Alert>
      )}

      {/* Genel Funnel — 5 KPI */}
      <Typography variant="h6" fontWeight={700} mb={1}>Genel Funnel (30 gün)</Typography>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <EmailIcon sx={{ color: "#1565C0", fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>GÖNDERILEN</Typography>
            </Box>
            <Typography variant="h4" fontWeight={800}>{fmtNum(o.smtp_ok)}</Typography>
            {o.smtp_failed > 0 && (
              <Typography variant="caption" color="#ef4444">{o.smtp_failed} failed</Typography>
            )}
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <MarkEmailReadIcon sx={{ color: "#10b981", fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>TESLİM</Typography>
            </Box>
            <Typography variant="h4" fontWeight={800} color="#10b981">{fmtNum(o.delivered)}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <VisibilityIcon sx={{ fontSize: 18, color: openColor(overallOpenRate) }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>AÇILDI</Typography>
            </Box>
            <Typography variant="h4" fontWeight={800} sx={{ color: openColor(overallOpenRate) }}>
              {fmtNum(o.opened)}
            </Typography>
            <Typography variant="caption" sx={{ color: openColor(overallOpenRate), fontWeight: 700 }}>
              %{overallOpenRate} open rate
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <TouchAppIcon sx={{ fontSize: 18, color: clickColor(overallClickRate) }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>TIKLANDI</Typography>
            </Box>
            <Typography variant="h4" fontWeight={800} sx={{ color: clickColor(overallClickRate) }}>
              {fmtNum(o.clicked)}
            </Typography>
            <Typography variant="caption" sx={{ color: clickColor(overallClickRate), fontWeight: 700 }}>
              %{overallClickRate} click rate
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <CancelIcon sx={{ fontSize: 18, color: bounceColor(overallBounceRate) }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>BOUNCE</Typography>
            </Box>
            <Typography variant="h4" fontWeight={800} sx={{ color: bounceColor(overallBounceRate) }}>
              {fmtNum(o.bounced)}
            </Typography>
            <Typography variant="caption" sx={{ color: bounceColor(overallBounceRate), fontWeight: 700 }}>
              %{overallBounceRate} bounce rate
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Spam + Unsubscribe (varsa uyar) */}
      {(o.spam > 0 || o.unsubscribed > 0) && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {o.spam > 0 && <span>📛 <strong>{o.spam}</strong> spam işareti aldı. </span>}
          {o.unsubscribed > 0 && <span>🚫 <strong>{o.unsubscribed}</strong> abonelikten çıktı.</span>}
          {(o.spam > 5 || o.unsubscribed > 20) && " Liste hijyeni veya içerik kalitesi gözden geçirilmeli."}
        </Alert>
      )}

      {/* Kampanya bazlı performans tablosu */}
      <Card sx={{ borderRadius: 3, overflow: "hidden", mb: 3 }}>
        <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <Typography variant="h6" fontWeight={700}>Kampanya Performansı</Typography>
          <Typography variant="caption" color="text.secondary">
            Son 30 kampanya — open/click rate sektör eşikleriyle renklendirilmiş
          </Typography>
        </Box>
        {data.campaigns.length === 0 ? (
          <Box sx={{ p: 3 }}><Alert severity="info">Henüz kampanya yok</Alert></Box>
        ) : (
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell><strong>Kampanya</strong></TableCell>
                <TableCell align="right"><strong>Gönd.</strong></TableCell>
                <TableCell align="right"><strong>Teslim</strong></TableCell>
                <TableCell align="center"><strong>Open Rate</strong></TableCell>
                <TableCell align="center"><strong>Click Rate</strong></TableCell>
                <TableCell align="center"><strong>Bounce</strong></TableCell>
                <TableCell><strong>Tarih</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.campaigns.map(c => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Typography fontSize={13} fontWeight={600} sx={{ lineHeight: 1.2 }}>
                      {c.name || c.subject || `#${c.id}`}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {c.subject?.slice(0, 50)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(c.sent)}</TableCell>
                  <TableCell align="right">{fmtNum(c.delivered)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`%${c.open_rate}`}
                      size="small"
                      sx={{
                        bgcolor: openColor(c.open_rate), color: "white",
                        fontWeight: 700, minWidth: 50, fontSize: 11
                      }}
                    />
                    <Typography variant="caption" display="block" color="text.disabled">
                      {fmtNum(c.opened)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`%${c.click_rate}`}
                      size="small"
                      sx={{
                        bgcolor: clickColor(c.click_rate), color: "white",
                        fontWeight: 700, minWidth: 50, fontSize: 11
                      }}
                    />
                    <Typography variant="caption" display="block" color="text.disabled">
                      {fmtNum(c.clicked)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`%${c.bounce_rate}`}
                      size="small"
                      sx={{
                        bgcolor: bounceColor(c.bounce_rate), color: "white",
                        fontWeight: 700, minWidth: 50, fontSize: 11
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: "text.disabled" }}>
                    {fmtDate(c.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Sektör tipiği rehberi */}
      <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#f8fafc" }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1}>
          📚 B2B Email Performans Sektör Tipiği
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Open Rate</Typography>
            <Typography fontSize={13}>
              %15-25 normal | <strong style={{ color: "#10b981" }}>%30+ çok iyi</strong>
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Click Rate</Typography>
            <Typography fontSize={13}>
              %2-5 normal | <strong style={{ color: "#10b981" }}>%7+ çok iyi</strong>
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Bounce Rate</Typography>
            <Typography fontSize={13}>
              %2-5 normal | <strong style={{ color: "#ef4444" }}>%10+ liste sorunu</strong>
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Spam Rate</Typography>
            <Typography fontSize={13}>
              %0.1- normal | <strong style={{ color: "#ef4444" }}>%0.5+ içerik problemi</strong>
            </Typography>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
}
